import { Calendar } from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import { RefObject, useEffect } from 'react';
import { debounce } from 'src/components/muiTheme/utils';

const SCROLL_RELAYOUT_DEBOUNCE_MS = 150;

function getCalendarRootElement(
  calendarRef: RefObject<FullCalendar | null>
): HTMLElement | undefined {
  const api = calendarRef.current?.getApi();
  if (!api) {
    return undefined;
  }

  return (api as Calendar).el;
}

export function relayoutFullCalendar(
  calendarRef: RefObject<FullCalendar | null>
): void {
  calendarRef.current?.requestResize();
}

export function bindFullCalendarScrollRelayout(
  calendarEl: HTMLElement,
  relayout: () => void
): () => void {
  const debouncedRelayout = debounce(relayout, SCROLL_RELAYOUT_DEBOUNCE_MS);
  const scrollers = calendarEl.querySelectorAll('.fc-scroller');
  const cleanups: (() => void)[] = [];
  const supportsScrollEnd = 'onscrollend' in window;

  scrollers.forEach((scroller) => {
    if (supportsScrollEnd) {
      scroller.addEventListener('scrollend', relayout, { passive: true });
      cleanups.push(() => scroller.removeEventListener('scrollend', relayout));
    } else {
      scroller.addEventListener('scroll', debouncedRelayout, { passive: true });
      cleanups.push(() => {
        scroller.removeEventListener('scroll', debouncedRelayout);
      });
    }
  });

  return () => {
    cleanups.forEach((cleanup) => cleanup());
    if (!supportsScrollEnd) {
      debouncedRelayout.clear();
    }
  };
}

export function useFullCalendarScrollRelayout(
  calendarRef: RefObject<FullCalendar | null>,
  {
    enabled = true,
    view,
  }: {
    enabled?: boolean;
    view?: string;
  } = {}
): void {
  useEffect(() => {
    if (!enabled || view === 'listWeek') {
      return;
    }

    let cleanup: (() => void) | undefined;
    let rafId = 0;

    const setup = () => {
      const calendarEl = getCalendarRootElement(calendarRef);

      if (!calendarEl) {
        rafId = requestAnimationFrame(setup);
        return;
      }

      cleanup = bindFullCalendarScrollRelayout(calendarEl, () => {
        relayoutFullCalendar(calendarRef);
      });
    };

    setup();

    return () => {
      cancelAnimationFrame(rafId);
      cleanup?.();
    };
  }, [calendarRef, enabled, view]);
}
